import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FocusLock from 'react-focus-lock';
import Button from '~/Components/Global/Form/Button';
import { closeModal, setModalData } from '~/Redux/Modules/Modal';
import { TASK_SETTINGS_MODAL } from '~/Config/Modals';
import SnoozeCalendar from '~/Components/Global/Assets/Svgs/SnoozeCalendar';
import moment from '~/Providers/Moment';
import { showRepeatOptionsModal, showAddAlarmModal } from '~/Redux/Modules/Tasks/actions';
import { DATE_FORMAT } from '~/Redux/Constants';
import Checkbox from '~/Components/Global/Form/Checkbox';
import '~/Components/Global/Form/ArtfulCheckbox.scss';
import { humanDateFormats, HUMAN_DATE_WITH_DAY_FORMAT } from '~/Redux/Modules/UserOptions';
import { MONTH_TODO, WEEK_TODO } from '~/Config/Checklists';
import { showConfirmWithSettings } from '~/Redux/Modules/EventConfirmation';
import AlarmOptions from '~/Components/Global/AlarmOptions/AlarmOptions';
import { mapCalendarsToList, findCalendarInList } from '~/Redux/Modules/CalendarAccounts';
import { darkGray } from '~/Styles/Colors.js';
import TaskDatePicker from './TaskDatePicker';
import './TaskSettingsModal.scss';

const handleKeyboardClick = e => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.target.click();
  }
};

const extractModalData = (isOpen, modalInfo) => {
  const isValidModal = isOpen && modalInfo?.modalType === TASK_SETTINGS_MODAL;
  return {
    task: isValidModal ? modalInfo.task : null,
    onSaveCallback: isValidModal ? modalInfo.onSave : null,
    onCancelCallback: isValidModal ? modalInfo.onCancel : null,
    positionRect: isValidModal ? modalInfo.positionRect : null,
  };
};

const TaskSettingsModal = () => {
  const dispatch = useDispatch();

  const isOpen = useSelector(state => state.Modal.modalsOpen.includes(TASK_SETTINGS_MODAL));
  const modalInfo = useSelector(state => state.Modal.modalData);
  const isEventConfirmationOpen = useSelector(state => state.EventConfirmation.heading);

  const [selected, setSelected] = useState(moment());
  const [rolloverEnabled, setRolloverEnabled] = useState(false);
  const [repeatRule, setRepeatRule] = useState('');
  const [pendingRepeatRule, setPendingRepeatRule] = useState(null);
  const [pendingRepeatConfirm, setPendingRepeatConfirm] = useState(null);
  const [pendingAlarms, setPendingAlarms] = useState([]);
  const [pendingDeletedAlarms, setPendingDeletedAlarms] = useState([]);
  const [pendingRollover, setPendingRollover] = useState(null);

  const currentTaskId = useRef(null);
  // The date picked before Alarm or Repeat was opened. Those modals hand this one fresh
  // modalData when they return, which re-runs the open effect below; without this it
  // put the picker back on the task's own date.
  const returningFrom = useRef(null);

  const { task, onSaveCallback, onCancelCallback, positionRect } = extractModalData(
    isOpen,
    modalInfo
  );

  const userOptions = useSelector(state => state.UserOptions.data);
  const dateFormats = useSelector(humanDateFormats);
  const use_intl_time = userOptions?.use_intl_time || false;
  const calendarAccounts = useSelector(state => state.CalendarAccounts);
  const calendars = mapCalendarsToList(calendarAccounts);
  const defaultCalendar = findCalendarInList(calendars, userOptions?.calendar_id);
  const defaultCalendarColor = defaultCalendar?.color || darkGray;

  const getCurrentAlarms = () => {
    let baseAlarms;

    if (pendingAlarms.length > 0) {
      baseAlarms = pendingAlarms;
    } else {
      const originalAlarms = task?.alarms || [];
      baseAlarms = originalAlarms.filter(
        alarm => !pendingDeletedAlarms.some(deleted => deleted.id === alarm.id)
      );
    }

    const processedAlarms = baseAlarms.map(alarm => {
      const isGoalTask = task?.type === MONTH_TODO || task?.type === WEEK_TODO;
      if (task?.repeat_rule && alarm.target_datetime && !isGoalTask) {
        const alarmMoment = moment(alarm.target_datetime, 'YYYY-MM-DD HH:mm:ss');
        const taskMoment = moment(task.start, DATE_FORMAT);

        if (!alarmMoment.isSame(taskMoment, 'day')) {
          const timeOnly = alarmMoment.format('HH:mm:ss');
          const newTargetDatetime = `${taskMoment.format('YYYY-MM-DD')} ${timeOnly}`;
          return {
            ...alarm,
            target_datetime: newTargetDatetime,
          };
        }
      }
      return alarm;
    });

    const isGoalTask = task?.type === MONTH_TODO || task?.type === WEEK_TODO;
    if (isGoalTask) {
      return processedAlarms;
    }

    const today = moment().startOf('day');
    const validAlarms = processedAlarms.filter(alarm => {
      const alarmDate = moment(alarm.target_datetime, 'YYYY-MM-DD HH:mm:ss').startOf('day');
      return alarmDate.isSameOrAfter(today);
    });

    return validAlarms;
  };

  const hasAlarmsChanged = () => {
    return pendingAlarms.length > 0 || pendingDeletedAlarms.length > 0;
  };

  const resetAllModalState = () => {
    setPendingAlarms([]);
    setPendingDeletedAlarms([]);
    setPendingRollover(null);
    setPendingRepeatRule(null);
    setPendingRepeatConfirm(null);

    const globalAutoRollover = userOptions?.auto_rollover;
    setRolloverEnabled(!!globalAutoRollover);
    setRepeatRule('');
    setSelected(moment());
  };

  const handleRolloverToggle = () => {
    const newValue = !rolloverEnabled;
    setRolloverEnabled(newValue);
    setPendingRollover(newValue);
  };

  const handleShowAddAlarmModal = () => {
    const currentAlarms = getCurrentAlarms();

    // On the picked date: an alarm added now belongs to the day the task is moving to.
    // Alarm defaults its date to `start` and hands this task back, so for a repeating
    // task getCurrentAlarms also rebases the alarms onto it. It is the date picked when
    // Alarm opened; picking another date afterwards does not move the alarm.
    const taskWithPendingChanges = {
      ...task,
      ...(selected && { start: selected.format(DATE_FORMAT) }),
      ...(pendingRepeatRule !== null && { repeat_rule: pendingRepeatRule }),
      ...(pendingRollover !== null && { auto_rollover: pendingRollover }),
      alarms: currentAlarms,
    };

    returningFrom.current = { taskId: task?.id, selected };
    dispatch(
      showAddAlarmModal(taskWithPendingChanges, onSaveCallback, onCancelCallback, positionRect)
    );
  };

  const handleAlarmDeletion = alarmToDelete => {
    if (alarmToDelete.id) {
      const newDeletedAlarms = [...pendingDeletedAlarms, alarmToDelete];
      setPendingDeletedAlarms(newDeletedAlarms);
    } else {
      const updatedPendingAlarms = pendingAlarms.filter(
        alarm => alarm.target_datetime !== alarmToDelete.target_datetime
      );
      setPendingAlarms(updatedPendingAlarms);
    }
  };

  useEffect(() => {
    if (isOpen && modalInfo?.modalType === TASK_SETTINGS_MODAL) {
      document.body.classList.add('task-settings-open');
      const currentTask = modalInfo.task;
      const newTaskId = currentTask?.id;

      if (currentTaskId.current && currentTaskId.current !== newTaskId) {
        resetAllModalState();
      }
      currentTaskId.current = newTaskId;

      const baseRepeatRule =
        typeof currentTask?.repeat_rule === 'string' ? currentTask.repeat_rule : '';
      const overrideRepeatRule =
        typeof modalInfo?.pendingRepeatRule === 'string' ? modalInfo.pendingRepeatRule : '';

      setRepeatRule(
        modalInfo?.pendingRepeatRule !== undefined ? overrideRepeatRule : baseRepeatRule
      );

      // Back from Alarm or Repeat: this modal stayed mounted, so the picked date and the
      // rollover checkbox still hold what the user set. Only the date needs putting back.
      const returning =
        returningFrom.current && returningFrom.current.taskId === currentTask?.id
          ? returningFrom.current
          : null;
      returningFrom.current = null;

      if (returning) {
        setSelected(returning.selected);
      } else if (currentTask?.start) {
        const taskStartDate = moment(currentTask.start, DATE_FORMAT);
        if (currentTask.type === WEEK_TODO) {
          if (userOptions?.use_monday_start) {
            setSelected(taskStartDate.clone().add(1, 'day'));
          } else {
            setSelected(taskStartDate);
          }
        } else {
          setSelected(taskStartDate);
        }
      } else {
        setSelected(moment());
      }

      if (returning) {
        // The task Alarm hands back carries the pending rollover as a boolean, which the
        // 3-state check below would read as "use the default".
      } else if (modalInfo?.pendingRollover !== undefined) {
        setPendingRollover(modalInfo.pendingRollover);
        setRolloverEnabled(!!modalInfo.pendingRollover);
      } else {
        // Handle 3-state system: -1=OFF, 0=use default, 1=ON
        //
        // A task without the field is at the default (0). The create response omits it:
        // TaskController::create returns the model without reloading the column default.
        // Only the user's own click may set pendingRollover. Setting it here counted a
        // rollover change nobody made, and Save sent that change in place of the new
        // date. That was every first snooze of a new task while global rollover is on
        // (ARTFLT-1623).
        const autoRollover = currentTask?.auto_rollover ?? 0;
        if (autoRollover === -1) {
          // Explicitly OFF - show checkbox as unchecked
          setRolloverEnabled(false);
        } else if (autoRollover === 1) {
          // Explicitly ON - show checkbox as checked
          setRolloverEnabled(true);
        } else {
          // Use global default (0) - show checkbox based on global setting
          const globalAutoRollover = userOptions?.auto_rollover;
          setRolloverEnabled(!!globalAutoRollover);
        }
      }

      if (modalInfo.pendingAlarms) {
        setPendingAlarms(modalInfo.pendingAlarms);
      }

      if (modalInfo?.pendingRepeatRule !== undefined) {
        setPendingRepeatRule(modalInfo.pendingRepeatRule);
      }
      if (modalInfo?.pendingRepeatConfirm !== undefined) {
        setPendingRepeatConfirm(modalInfo.pendingRepeatConfirm);
      }
    }
    return () => {
      currentTaskId.current = null;
      document.body.classList.remove('task-settings-open');
    };
  }, [isOpen, modalInfo, userOptions?.auto_rollover, userOptions?.use_monday_start, dispatch]);

  const handleDateChange = newDate => {
    setSelected(newDate);
  };

  const handleCancelClick = () => {
    resetAllModalState();
    currentTaskId.current = null;

    dispatch(closeModal(TASK_SETTINGS_MODAL));
    dispatch(setModalData(null));
    if (onCancelCallback) onCancelCallback();
  };

  const collectAllChanges = () => {
    const updates = {};

    if (pendingRepeatRule !== null) {
      updates.repeat_rule = pendingRepeatRule;
    }

    if (pendingRollover !== null) {
      // Use 3-state system like mobile and RepeatRulePopup:
      // -1 = explicitly OFF (never rollover)
      //  0 = use global default (not applicable here since we track explicit changes)
      //  1 = explicitly ON (always rollover)
      updates.auto_rollover = pendingRollover ? 1 : -1;
      updates.rolloverChanged = true;
    }

    if (hasAlarmsChanged()) {
      updates.alarms = getCurrentAlarms();
    }

    return updates;
  };

  const hasAnyChanges = updatedFields => {
    return Object.keys(updatedFields).length > 0;
  };

  // TaskItem sends every request for the Save, the date and these changes together
  // (taskSettingsUpdates). `changes` is null when only the date can have changed.
  const closeModalAndCleanup = changes => {
    if (onSaveCallback && selected) {
      onSaveCallback(selected, changes);
    }
    dispatch(closeModal(TASK_SETTINGS_MODAL));
    resetAllModalState();
    dispatch(setModalData(null));
  };

  const handleSaveClick = () => {
    const updatedFields = collectAllChanges();

    const isExistingRecurringTask = task?.repeats;
    if (isExistingRecurringTask && hasAnyChanges(updatedFields)) {
      dispatch(
        showConfirmWithSettings({
          heading: 'Edit recurring task',
          onConfirm: confirm => {
            closeModalAndCleanup({ fields: updatedFields, confirm });
          },
          onCancel: () => {},
          model: 'Task',
        })
      );
    } else if (hasAnyChanges(updatedFields)) {
      closeModalAndCleanup({ fields: updatedFields, confirm: 'all' });
    } else {
      closeModalAndCleanup(null);
    }
  };

  const isTaskSingleInstance = () => {
    return task?.recurrence_id && task?.ics !== task?.id;
  };

  const getRepeatDisplayText = rule => {
    if (isTaskSingleInstance()) {
      return 'Does not repeat';
    }

    if (!rule || typeof rule !== 'string') return 'Does not repeat';

    const parsed = rule.split(';').reduce((obj, piece) => {
      let parts = piece.split('=');
      obj[parts[0]] = parts[1];
      return obj;
    }, {});

    switch (parsed.FREQ) {
      case 'DAILY':
        return 'Repeats Daily';
      case 'WEEKLY':
        return 'Repeats Weekly';
      case 'MONTHLY':
        return 'Repeats Monthly';
      case 'YEARLY':
        return 'Repeats Yearly';
      default:
        return 'Does not repeat';
    }
  };

  const formatAlarmDateTime = targetDatetime => {
    const alarmMoment = moment(targetDatetime, 'YYYY-MM-DD HH:mm:ss');
    const dateFormat = dateFormats[HUMAN_DATE_WITH_DAY_FORMAT] || 'ddd M/DD/YYYY';
    const timeFormat = use_intl_time ? 'HH:mm' : 'h:mma';

    return `${alarmMoment.format(dateFormat)}, ${alarmMoment.format(timeFormat)}`;
  };

  const getSortedAlarms = alarms => {
    if (!alarms || alarms.length === 0) return [];

    return [...alarms].sort((a, b) => {
      const dateA = moment(a.created_at, 'YYYY-MM-DD HH:mm:ss');
      const dateB = moment(b.created_at, 'YYYY-MM-DD HH:mm:ss');
      return dateB.diff(dateA);
    });
  };

  const handleDeleteAlarm = (e, alarmToDelete) => {
    e.stopPropagation();
    handleAlarmDeletion(alarmToDelete);
  };

  const isRepeatRowDisabled = () => {
    return isTaskSingleInstance();
  };

  const handleRepeatClick = () => {
    if (!isRepeatRowDisabled()) {
      // Pass this modal's own callbacks: Repeat hands them back when it returns, and a
      // null onSave there meant Save never reached TaskItem (ARTFLT-1623).
      returningFrom.current = { taskId: task?.id, selected };
      dispatch(showRepeatOptionsModal(task, onSaveCallback, onCancelCallback, positionRect));
    }
  };

  const getRolloverAriaLabel = () => {
    return `${rolloverEnabled ? 'Disable' : 'Enable'} rollover for incomplete tasks`;
  };

  const handleStopPropagation = e => {
    e.stopPropagation();
  };

  const getOpenToDate = () => {
    return task && task.start ? moment(task.start, DATE_FORMAT) : selected;
  };

  if (!isOpen || !modalInfo || modalInfo.modalType !== TASK_SETTINGS_MODAL) {
    return null;
  }

  let style = {};
  if (positionRect) {
    style = {
      position: 'absolute',
      top:
        positionRect.top > window.innerHeight - 550 ? window.innerHeight - 550 : positionRect.top,
      left:
        positionRect.left > window.innerWidth - 160 ? window.innerWidth - 160 : positionRect.left,
      transform: 'translateX(-50%)',
    };
  }

  return (
    <div
      className={`task-settings-modal-wrapper ${isEventConfirmationOpen ? 'disabled' : ''}`}
      onClick={handleCancelClick}
    >
      <div
        className={`task-settings-modal ${isEventConfirmationOpen ? 'disabled' : ''}`}
        style={style}
        onClick={e => e.stopPropagation()}
        data-stop-drop-down-close
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-settings-modal-title"
        aria-hidden={isEventConfirmationOpen ? 'true' : undefined}
      >
        <FocusLock>
          <div className="task-settings-modal__header" id="task-settings-modal-title">
            <span className="header-with-icon">
              <SnoozeCalendar className="header-icon" />
              Snooze until
            </span>
          </div>
          <div className="task-settings-modal__body">
            <TaskDatePicker
              onChange={handleDateChange}
              selected={selected}
              openToDate={getOpenToDate()}
              defaultCalendarColor={defaultCalendarColor}
            />

            <div className="task-settings-modal__options-container">
              <div
                className={`option-row ${isRepeatRowDisabled() ? 'option-row--disabled' : ''}`}
                role={isRepeatRowDisabled() ? undefined : 'button'}
                tabIndex={isRepeatRowDisabled() ? undefined : '0'}
                onClick={handleRepeatClick}
                onKeyDown={handleKeyboardClick}
                aria-label={isRepeatRowDisabled() ? undefined : 'Set repeat options'}
              >
                <span
                  className={`material-icons repeat-icon ${
                    repeatRule ? 'repeat-icon--active' : ''
                  }`}
                >
                  repeat
                </span>
                <span className="option-label">{getRepeatDisplayText(repeatRule)}</span>
                {!isRepeatRowDisabled() && (
                  <span className="material-icons-outlined repeat-chevron">chevron_right</span>
                )}
              </div>
              <div
                className="option-row"
                role="button"
                tabIndex="0"
                onClick={handleRolloverToggle}
                onKeyDown={handleKeyboardClick}
                aria-label={getRolloverAriaLabel()}
              >
                <div className="artful-checkbox-wrapper" onClick={handleStopPropagation}>
                  <Checkbox
                    name="rollover"
                    value={rolloverEnabled}
                    onChange={handleRolloverToggle}
                    formatted={false}
                  />
                </div>
                <span className="option-label option-label__rollover">
                  Rollover unchecked tasks
                </span>
              </div>
              <AlarmOptions
                getCurrentAlarms={getCurrentAlarms}
                getSortedAlarms={getSortedAlarms}
                handleShowAddAlarmModal={handleShowAddAlarmModal}
                handleDeleteAlarm={handleDeleteAlarm}
                formatAlarmDateTime={formatAlarmDateTime}
              />
            </div>
          </div>
          <div className="task-settings-modal__buttons">
            <Button text="CANCEL" className="btn btn-default" onClick={handleCancelClick} />
            <div className="spacer" />
            <Button text="SAVE" className="btn btn-default" onClick={handleSaveClick} />
          </div>
        </FocusLock>
      </div>
    </div>
  );
};

export default TaskSettingsModal;
